import React, { Dispatch, memo, SetStateAction, useCallback, useEffect } from 'react';
import './edit-geometry-toolbar.scss';
import { useTheme } from '@context/themeContext';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectDrawMode } from '@redux/selectors/selectors';
import Button from '../../buttons/Button';
import { DrawControlsClass } from '@utils/DrawControlsClass';
import { useLayersInteractivity } from '@hooks/map/useLayersInteractivity';
import { useToastContext } from '@context/toastContext';
import { FeatureProperties } from '@redux/map/types';
import { useConditionalBlocker } from '@context/navigationBlockerContext';
import { useMapLayersClickabilityContext } from '@context/mapLayersClickability';

type Props = {
    children?: React.ReactElement | null;
    drawnFeatures: GeoJSON.Feature<any, Partial<FeatureProperties>>[];
    setFeature: Dispatch<SetStateAction<string>>;
    defaultValue?: string;
};

const EditGeometryToolbar: React.FC<Props> = ({
    children,
    drawnFeatures,
    setFeature,
    defaultValue = '',
}) => {
    const drawMode = useTypedSelector(selectDrawMode);
    const { cleanDrawnFeatures, recoverSettingsAfterDrawing, resetCutMode } =
        useLayersInteractivity();
    const { recoverLayersInteractivity } = useMapLayersClickabilityContext();
    const { theme } = useTheme();
    const { t } = useTranslation();
    const { addToast } = useToastContext();

    const modeString = `Edit mode: ${drawMode}`;

    const keyPressHandler = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                recoverSettingsAfterDrawing();
                recoverLayersInteractivity();
            }
        },
        [recoverLayersInteractivity]
    );

    useEffect(() => {
        document.addEventListener('keyup', keyPressHandler);

        return () => {
            document.removeEventListener('keyup', keyPressHandler);
        };
    }, [keyPressHandler]);

    const applyChangesClickHandler = useCallback(async () => {
        const feature = drawnFeatures?.[0] ? JSON.stringify(drawnFeatures?.[0]) : '';
        setFeature(feature);

        DrawControlsClass.setDrawControlVisibility({ drawType: 'AllControls', visible: false });
        resetCutMode();
        addToast(t('forms.geometry-updated-toast'), 'success');
    }, [drawnFeatures]);

    const discardGeometryChanges = useCallback(() => {
        cleanDrawnFeatures();
        recoverSettingsAfterDrawing();
        recoverLayersInteractivity();

        setFeature(defaultValue);
    }, [defaultValue, recoverLayersInteractivity]);

    useConditionalBlocker({
        checkFn: () => !!drawnFeatures.length,
        onProceedFn: discardGeometryChanges,
    });

    return (
        <div className={`edit-geometry-toolbar ${theme}`} data-testid="editGeometryToolbar">
            {children}
            {modeString}
            <Button
                text={t('map.apply-changes-btn')}
                onClick={applyChangesClickHandler}
                // disabled={drawnFeatures?.length}
                colorType="transparent"
                testId="applyGeometryUpdateBtn"
            />
            <Button
                text={t('map.discard-geometry-change')}
                onClick={discardGeometryChanges}
                disabled={!drawnFeatures?.length}
                colorType="transparent"
                testId="cancelGeometryUpdateBtn"
            />
        </div>
    );
};

export default memo(EditGeometryToolbar);
